import {
  ccc,
  hexFrom,
  hashTypeToBytes,
  Cell,
  Hex,
} from "@ckb-ccc/connector-react";
import { ckbJsVmScript, contractScript, xudtScript } from "./ckbClient";

export const findJoinAirdropCells = async (
  client: ccc.Client,
  udtTypeHash: string,
): Promise<Cell[]> => {
  const mainScript = {
    codeHash: ckbJsVmScript.script.codeHash,
    hashType: ckbJsVmScript.script.hashType,
    args: hexFrom(
      "0x0000" +
        contractScript.codeHash.slice(2) +
        hexFrom(hashTypeToBytes(contractScript.hashType)).slice(2) +
        udtTypeHash.slice(2, 42),
    ),
  };

  const iterator = client.findCells({
    script: mainScript,
    scriptType: "lock",
    scriptSearchMode: "prefix",
  });

  const cells: Cell[] = [];

  for await (const cell of iterator) {
    cells.push(cell);
  }
  return cells;
};

export const createJoinAirdropCell = async (
  signer: ccc.Signer,
  udtTypeHash: string,
  sinceValue: bigint,
): Promise<string> => {
  const signerAddressObj = await signer.getRecommendedAddressObj();
  const signerLock = signerAddressObj.script;

  // Args: UDT type hash (20 bytes) + original lock hash (20 bytes) + since (8 bytes)
  const originalLockHash = signerLock.hash().slice(0, 42); // first 20 bytes + 0x
  const sinceHex = sinceValue.toString(16).padStart(16, "0"); // 8 bytes hex

  const mainScript = {
    codeHash: ckbJsVmScript.script.codeHash,
    hashType: ckbJsVmScript.script.hashType,
    args: hexFrom(
      "0x0000" +
        contractScript.codeHash.slice(2) +
        hexFrom(hashTypeToBytes(contractScript.hashType)).slice(2) +
        udtTypeHash.slice(2) +
        originalLockHash.slice(2) +
        sinceHex,
    ),
  };

  const tx = ccc.Transaction.from({
    outputs: [
      {
        capacity: 250n * 10n ** 8n, // 250 CKB
        lock: mainScript,
      },
    ],
  });

  await tx.completeInputsByCapacity(signer);
  await tx.completeFeeBy(signer, 1000);
  const txHash = await signer.sendTransaction(tx);
  return txHash;
};

export const mintUDTToJoinCell = async (
  joinCell: ccc.Cell,
  udtSigner: ccc.Signer,
  amount: bigint,
): Promise<string> => {
  const udtTypeScript = {
    codeHash: xudtScript.script.codeHash,
    hashType: xudtScript.script.hashType,
    args: (await udtSigner.getRecommendedAddressObj()).script.hash(), // UDT owner
  };

  const tx = ccc.Transaction.from({
    inputs: [
      {
        previousOutput: joinCell.outPoint,
      },
    ],
    outputs: [
      {
        lock: joinCell.cellOutput.lock,
        type: udtTypeScript,
      },
    ],
    outputsData: [ccc.numToBytes(amount, 16)], // UDT amount
    cellDeps: [
      ...ckbJsVmScript.script.cellDeps.map((c: any) => c.cellDep),
      ...contractScript.cellDeps.map((c: any) => c.cellDep),
      ...xudtScript.script.cellDeps.map((c: any) => c.cellDep),
    ],
  });

  await tx.completeInputsByCapacity(udtSigner);
  await tx.completeFeeBy(udtSigner, 1000); // todo: we should let the join-cell pay the fee
  const txHash = await udtSigner.sendTransaction(tx);
  return txHash;
};

export function getListedUDTTypeHash(): Hex {
  // This should be the type hash of the UDT token used for the airdrop
  // You can get this from getDefaultUDTTypeHashFromLock
  const envTypeHash = process.env.REACT_APP_TARGET_UDT_TYPE_HASH;
  if (
    envTypeHash &&
    envTypeHash.startsWith("0x") &&
    envTypeHash.length === 66
  ) {
    return envTypeHash as Hex;
  } else {
    throw new Error(
      "REACT_APP_TARGET_UDT_TYPE_HASH is not set correctly in environment variables",
    );
  }
}

export function getDefaultUDTTypeHashFromLock(lock: ccc.Script): Hex {
  const script: ccc.ScriptLike = {
    codeHash: xudtScript.script.codeHash,
    hashType: xudtScript.script.hashType,
    args: lock.hash(), // Extract UDT type hash from args
  };

  return ccc.Script.from(script).hash();
}
